import React, { useEffect, useState, useRef, useCallback } from 'react'

import ToutArea from './ToutArea'
import PropTypes from 'prop-types'
import { isMobile, isTablet, useMobileOrientation } from 'react-device-detect'
import { config } from './../Init/Init'
import { constants } from '../../componentsConstants'

function Tout(props) {
  const [toutWidth, setToutWidth] = useState(window.innerWidth),
    myRef = useRef(),
    { isPortrait } = useMobileOrientation()

  const handleResize = useCallback(
    entries => {
      try {
        for (let entry of entries) {
          const cr = entry.contentRect
          setToutWidth(cr.width)
        }
      } catch (e) {
        console.log(e)
      }
    },
    [setToutWidth]
  )

  useEffect(() => {
    if (window.ResizeObserver) {
      const ro = new ResizeObserver(handleResize)
      ro.observe(myRef.current)

      return () => {
        if (ro) {
          ro.disconnect()
        }
      }
    }
  }, [handleResize])

  function alwaysUseFirstBreakpoint() {
    if (isMobile && isPortrait && !isTablet) {
      // for mobile phone portrait, we want to always display portrait banners, which is breakpoint 0 (bgImageMobilePortraitJpg)
      return true
    }
    return false
  }

  function chooseBestBreakpointKey(keysArray, maxWidth) {
    if (alwaysUseFirstBreakpoint()) {
      // for mobile phone portrait, we want to always display portrait banners, which is breakpoint 0 (bgImageMobilePortraitJpg)
      return '0'
    } else {
      let numKeysArray = keysArray.map(x => parseInt(x))
      const keysWithinMaxwidth = numKeysArray.filter(breakpoint => breakpoint < maxWidth)
      const sortedBreakpoints = keysWithinMaxwidth.sort((a, b) => b - a)
      return sortedBreakpoints[0].toString()
    }
  }

  function getPositionedElements(toutArray, layer) {
    let newObjectArray = []

    for (let i = 0; i < toutArray.length; i++) {
      let slotObj = toutArray[i]

      let breakpointKey = chooseBestBreakpointKey(Object.keys(slotObj), toutWidth)
      let toutObj = slotObj[breakpointKey]

      if (toutObj.area !== 'none') {
        if (
          (toutObj.area === 'fill' && layer === 'background') ||
          (toutObj.area !== 'fill' && layer === 'foreground')
        ) {
          newObjectArray.push(toutObj)
        }
      }
    }

    return newObjectArray
  }

  function foregroundObjectArray() {
    // Check if the tout is visible.
    if (toutWidth === 0) {
      return []
    }
    if (!props.toutData) {
      console.warn('[Tout] Missing tout data', props.toutData, props.asset, props.toutId)
      if (props.onError) {
        props.onError()
      }
      return []
    }
    return getPositionedElements(
      props.toutData.map(obj => obj.slot),
      'foreground'
    )
  }

  function backgroundObjectArray() {
    // Check if the tout is visible.
    if (toutWidth === 0) {
      return []
    }
    if (!props.toutData) {
      console.warn('[Tout] Missing tout data', props.toutData, props.asset, props.toutId)
      if (props.onError) {
        props.onError()
      }
      return []
    }
    return getPositionedElements(
      props.toutData.map(obj => obj.slot),
      'background'
    )
  }

  function getGridAlignment() {
    let areaStrings = foregroundObjectArray()
      .filter(obj => obj.content.type !== 'placeholder')
      .map(obj => obj.area)
      .sort()
    if (areaStrings[0] === 'c' || areaStrings === []) {
      return 'ac-c jc-c'
    } else {
      if (areaStrings.every(area => area.substring(0, 1) === 'l')) {
        return 'ac-c jc-fs'
      } else if (areaStrings.every(area => area.substring(0, 1) === 'r')) {
        return 'ac-c jc-fe'
      } else if (areaStrings.every(area => area.substring(1, 2) === 't')) {
        return 'ac-fs jc-c'
      } else if (areaStrings.every(area => area.substring(1, 2) === 'b')) {
        return 'ac-fe jc-c'
      } else {
        return 'ac-c jc-c'
      }
    }
  }

  function parallaxSpacingClass() {
    if (props.isParallax && props.parallaxSpace) {
      return `b-n${props.parallaxSpace}0/100 t-n${props.parallaxSpace}0/100`
    } else if (props.isParallax) {
      return 'b-n10/100 t-n10/100'
    } else {
      return 'b-0 t-0'
    }
  }

  function bannerToutContainerHeightClass() {
    // Check if the tout is visible.
    if (toutWidth === 0) {
      return ''
    }
    if (!props.toutData) {
      console.warn('[Tout] Missing tout data', props.toutData, props.asset, props.toutId)
      if (props.onError) {
        props.onError()
      }
      return ''
    }

    // Add a height class based on the window width
    // For some reason, with the new 3000px wide banners, the banner height values get messed up, so we need to set them manually to match old behavior...
    if (alwaysUseFirstBreakpoint()) {
      // for mobile phone portrait, we want to always display portrait banners
      return 'h-176px'
    } else {
      // These breakpoints and heights were determined using responsive UI with the old banners
      if (toutWidth >= constants.screenMeasures['1441']) {
        return 'h-350px'
      } else if (toutWidth >= constants.screenMeasures['1171']) {
        return 'h-326px'
      } else if (toutWidth >= constants.screenMeasures['769']) {
        return 'h-286px'
      } else if (toutWidth >= constants.screenMeasures['666']) {
        return 'h-270px'
      } else {
        return 'h-176px'
      }
    }
  }

  return (
    <div
      ref={myRef}
      className={`tout-master-container ai-st dis-f fxg-1 jc-c mh-a mih-1/1 pos-r w-1/1 
      ${props.isBanner && config.enableBlurredBackground ? 'banner-tout-master-container' : ''}
      ${props.isBanner && bannerToutContainerHeightClass()}
      ${parallaxSpacingClass()}`}
      percentage={props.parallaxPosition}
      speed={props.isParallax ? 1 : 0}
    >
      {backgroundObjectArray().map((obj, index) => (
        <ToutArea
          key={index}
          dataObject={obj}
          asset={props.asset}
          onAction={props.onAction}
          isBanner={props.isBanner}
        />
      ))}

      <div
        className={`tout-positioned-objects-container ai-c dis-g fxg-1 gr-t-c gr-t-r ji-c w-1/1 ${getGridAlignment()}`}
        percentage={props.parallaxPosition}
        speed={props.isParallax ? 2 : 0}
        style={{
          maxWidth: props.isBanner ? '101rem' : 'auto',
          gridTemplateAreas: `'llttt lttt cttt rttt rrttt'
            'lltt ltt ctt rtt rrtt'
            'llt lt ct rt rrt'
            'll l c r rr'
            'llb lb cb rb rrb'
            'llbb lbb cbb rbb rrbb'
            'llbbb lbbb cbbb rbbb rrbbb'`
        }}
      >
        {foregroundObjectArray().map((obj, index) => (
          <ToutArea
            parentKey={`${index}-${props.parentKey}`}
            key={`${index}-${props.parentKey}`}
            dataObject={obj}
            asset={props.asset}
            onAction={props.onAction}
          />
        ))}
      </div>
    </div>
  )
}

Tout.defaultProps = {
  defaultWidth: window.innerWidth,
  isParallax: false,
  parallaxPosition: null
}

Tout.propTypes = {
  isParallax: PropTypes.bool,
  parallaxPosition: PropTypes.number,
  parallaxSpace: PropTypes.number,
  toutData: PropTypes.array.isRequired
}

export default Tout
